import { useEffect, useRef } from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import type { AgentSession } from '@/lib/agents/types';
import { MessageBlock } from './MessageBlock';

interface Props {
  session: AgentSession;
}

export function MessageThread({ session }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
  }, [session.id, session.messages.length]);

  return (
    <ScrollArea.Root type="auto" className="relative h-full w-full overflow-hidden">
      <ScrollArea.Viewport className="!block h-full w-full">
        <div className="mx-auto w-full max-w-[760px] px-6 py-4">
          {session.messages.length === 0 ? (
            <EmptyState name={session.name} />
          ) : (
            <div className="divide-y divide-border/60">
              {session.messages.map((m, i) => {
                // Only the very last message can be the one being filled
                // by the live stream — and only if the session is in a
                // state where the model is actively producing tokens.
                const isLast = i === session.messages.length - 1;
                const isStreamingState =
                  session.status === 'streaming' ||
                  session.status === 'thinking' ||
                  session.status === 'tool_calling';
                return (
                  <MessageBlock key={m.id} message={m} streaming={isLast && isStreamingState} />
                );
              })}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        orientation="vertical"
        className="flex w-2.5 touch-none select-none p-0.5"
      >
        <ScrollArea.Thumb className="relative flex-1 rounded-full bg-border-strong/60" />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  );
}

function EmptyState({ name }: { name: string }) {
  return (
    <div className="mt-16 flex flex-col items-center text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
        <span className="text-xl font-bold">X</span>
      </div>
      <h2 className="text-[15px] font-semibold text-fg">{name}</h2>
      <p className="mt-1 max-w-sm text-[13px] text-fg-muted">
        Start a conversation. Use{' '}
        <kbd>/</kbd> for slash commands, <kbd>+</kbd> to attach files,
        or pick a different agent from the rail.
      </p>
    </div>
  );
}
